import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';

const TIER_COLORS = { solo: '#5B9ED0', club: '#4AC87A', pro: '#C8A84B', elite: '#7B6FD0' };
const TIER_LABELS = { solo: 'Solo', club: 'Club', pro: 'Pro', elite: 'Elite' };

export default function CoachMarketplace({ lang, currentUserId }) {
  const nl = lang === 'nl';
  const [coaches, setCoaches] = useState(null);
  const [hiring, setHiring] = useState(null);   // coach being hired (modal open)
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState({});           // { coachId: true }
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/coaches`)
      .then(r => r.json())
      .then(d => setCoaches(Array.isArray(d) ? d : []))
      .catch(() => setCoaches([]));
  }, []);

  const sendHireRequest = async () => {
    if (!hiring || !currentUserId) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await fetch(`${BASE}/coach-contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coach_id: hiring.id,
          client_id: currentUserId,
          client_type: 'individual',
          message: message.trim(),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSent(prev => ({ ...prev, [hiring.id]: true }));
        setHiring(null);
        setMessage('');
      } else {
        setSendError(data.error || (nl ? 'Fout. Probeer opnieuw.' : 'Error. Please try again.'));
      }
    } catch {
      setSendError(nl ? 'Verbindingsfout.' : 'Connection error.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <div style={{ fontSize: 11, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 16 }}>
        {nl ? 'COACHES VINDEN' : 'FIND A COACH'}
      </div>

      {coaches === null ? (
        <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '40px 0' }}>
          {nl ? 'Laden...' : 'Loading...'}
        </div>
      ) : coaches.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 16px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 16,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            {nl ? 'Nog geen coaches beschikbaar' : 'No coaches listed yet'}
          </div>
          <div style={{ color: C.silver, fontSize: 12 }}>
            {nl ? 'Coaches kunnen hun profiel zichtbaar maken via hun dashboard.' : 'Coaches can make their profile visible via their dashboard.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {coaches.map(coach => {
            const tierColor = TIER_COLORS[coach.tier] || _T.accent;
            const alreadySent = sent[coach.id];
            return (
              <div key={coach.id} style={{
                background: _T.bg3,
                border: `1px solid ${_T.cardBorderBot}`,
                borderRadius: 16,
                padding: '16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  {/* Avatar */}
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                    background: `${_T.accent}18`,
                    border: `2px solid ${_T.accent}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22,
                  }}>
                    {coach.avatar || '👤'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{coach.name}</span>
                      <span style={{
                        background: `${tierColor}20`,
                        border: `1px solid ${tierColor}55`,
                        borderRadius: 20, padding: '2px 8px',
                        fontSize: 9, fontWeight: 700, color: tierColor,
                      }}>
                        {TIER_LABELS[coach.tier] || coach.tier}
                      </span>
                    </div>
                    {coach.specialty && (
                      <div style={{ color: _T.accent, fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                        {coach.specialty}
                      </div>
                    )}
                    {coach.bio && (
                      <div style={{
                        color: C.silver2, fontSize: 12, lineHeight: 1.5, marginTop: 6,
                        display: '-webkit-box', WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {coach.bio}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
                      {coach.rate ? (
                        <div style={{ color: C.silver, fontSize: 12 }}>
                          <span style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>
                            {coach.currency === 'EUR' ? '€' : coach.currency}{Number(coach.rate).toFixed(0)}
                          </span>
                          <span style={{ color: C.silver }}> / {nl ? 'sessie' : 'session'}</span>
                        </div>
                      ) : (
                        <div style={{ color: C.silver, fontSize: 11 }}>
                          {nl ? 'Prijs op aanvraag' : 'Price on request'}
                        </div>
                      )}
                      {alreadySent ? (
                        <div style={{ color: '#4AC87A', fontSize: 12, fontWeight: 700 }}>
                          ✓ {nl ? 'Verzoek verstuurd' : 'Request sent'}
                        </div>
                      ) : (
                        <button
                          onClick={() => { setHiring(coach); setMessage(''); setSendError(null); }}
                          style={{
                            background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
                            border: 'none', borderRadius: 10,
                            color: '#0a1220', fontSize: 12, fontWeight: 700,
                            padding: '8px 16px', cursor: 'pointer',
                          }}
                        >
                          {nl ? 'Inhuren' : 'Hire'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hire modal */}
      {hiring && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          zIndex: 1000, padding: '0 0 0 0',
        }}
          onClick={e => { if (e.target === e.currentTarget) { setHiring(null); setMessage(''); } }}
        >
          <div style={{
            background: _T.bg2, borderRadius: '20px 20px 0 0',
            padding: '24px 20px 32px', width: '100%', maxWidth: 480,
          }}>
            <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 16 }}>
              {nl ? 'COACH INHUREN' : 'HIRE COACH'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: `${_T.accent}18`, border: `2px solid ${_T.accent}33`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>
                {hiring.avatar || '👤'}
              </div>
              <div>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>{hiring.name}</div>
                {hiring.specialty && <div style={{ color: _T.accent, fontSize: 11 }}>{hiring.specialty}</div>}
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.silver, marginBottom: 8 }}>
              {nl ? 'Stuur een bericht (optioneel)' : 'Send a message (optional)'}
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={nl ? 'Vertel de coach kort over je doelen...' : 'Briefly tell the coach about your goals...'}
              rows={4}
              style={{
                width: '100%', background: _T.bg4,
                border: `1px solid rgba(255,255,255,0.12)`,
                borderRadius: 12, color: C.white, fontSize: 13,
                padding: '12px', outline: 'none', resize: 'none',
                boxSizing: 'border-box', lineHeight: 1.5,
              }}
            />
            {sendError && (
              <div style={{ color: '#C05050', fontSize: 11, marginTop: 8 }}>{sendError}</div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={() => { setHiring(null); setMessage(''); }}
                style={{
                  flex: 1, background: 'none',
                  border: `1px solid rgba(255,255,255,0.12)`,
                  borderRadius: 12, color: C.silver,
                  fontSize: 13, padding: '12px', cursor: 'pointer',
                }}
              >
                {nl ? 'Annuleren' : 'Cancel'}
              </button>
              <button
                onClick={sendHireRequest}
                disabled={sending}
                style={{
                  flex: 2,
                  background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
                  border: 'none', borderRadius: 12,
                  color: '#0a1220', fontSize: 13, fontWeight: 700,
                  padding: '12px', cursor: sending ? 'not-allowed' : 'pointer',
                  opacity: sending ? 0.7 : 1,
                }}
              >
                {sending ? '...' : (nl ? 'Verzoek versturen' : 'Send request')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
